import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, ArrowRight, CheckCircle2, Sparkles, ShieldCheck, TrendingUp, Crown, Camera, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY_PREFIX = 'wakacasa.nextAction.dismissed.';

/**
 * "What's next?" strip — recommends the single most-impactful action for the
 * agent today based on verification status, profile completion, listing usage,
 * and leads engagement. Frontend-only scoring; pulls from the existing
 * /api/verification/status and /api/agent/leads endpoints.
 *
 * Recommendation order (first match wins):
 *  1. Verification rejected → urgent re-submit
 *  2. Verification expired → renew
 *  3. Listing limit reached → upgrade tier
 *  4. ≥1 stalled listing (3+ reveals, 0 messages) → refresh listing
 *  5. Verification not started → start verification
 *  6. Profile <70% complete → complete profile
 *  7. Verification pending review → meanwhile add photos to a popular listing
 *  8. Healthy state → encourage new listing
 */
export default function NextActionStrip({ profileCompletion, listingLimit, currentTier }) {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const tt = (en, es) => (language === 'es' ? es : en);

  const [verif, setVerif] = useState(null);
  const [leadsSummary, setLeadsSummary] = useState(null);
  const [topStalled, setTopStalled] = useState(null);
  const [topActive, setTopActive] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.allSettled([
      axios.get(`${API}/verification/status`, { headers }),
      axios.get(`${API}/agent/leads`, { headers }),
    ]).then(([v, l]) => {
      if (cancelled) return;
      if (v.status === 'fulfilled') setVerif(v.value.data);
      if (l.status === 'fulfilled') {
        const d = l.value.data;
        setLeadsSummary(d.summary);
        // First stalled (≥3 reveals AND 0 messages); fall back to "most-viewed".
        const stalled = (d.leads || []).find(x => x.reveals.count >= 3 && x.messages.count === 0);
        const active = (d.leads || []).find(x => x.engagement > 0);
        setTopStalled(stalled || null);
        setTopActive(active || null);
      }
    });
    return () => { cancelled = true; };
  }, [token]);

  // Compose the single recommendation
  const rec = (() => {
    const vstatus = verif?.status;
    const profilePct = profileCompletion?.percentage ?? 100;
    const limitReached = listingLimit?.limit != null && listingLimit.active >= listingLimit.limit;

    if (vstatus === 'rejected') return {
      tone: 'urgent',
      icon: AlertTriangle,
      title: tt('Verification rejected', 'Verificación rechazada'),
      message: tt(
        'Re-submit corrected documents to restore your gold badge.',
        'Reenvía documentos corregidos para restaurar tu insignia dorada.'
      ),
      cta: tt('Re-submit →', 'Reenviar →'),
      to: '/verification',
      testid: 'next-action-verification-rejected',
    };
    if (vstatus === 'expired') return {
      tone: 'urgent',
      icon: AlertTriangle,
      title: tt('Verification expired', 'Verificación expirada'),
      message: tt(
        'Renew now to keep the gold badge on every listing.',
        'Renueva ahora para mantener la insignia dorada en cada listado.'
      ),
      cta: tt('Renew →', 'Renovar →'),
      to: '/verification',
      testid: 'next-action-verification-expired',
    };
    if (limitReached) return {
      tone: 'warn',
      icon: Crown,
      title: tt('Listing limit reached', 'Límite de listados alcanzado'),
      message: tt(
        `You're at ${listingLimit.active}/${listingLimit.limit} listings. Upgrade for unlimited.`,
        `Estás en ${listingLimit.active}/${listingLimit.limit} listados. Mejora a ilimitado.`
      ),
      cta: tt('Upgrade tier →', 'Mejorar plan →'),
      to: '/upgrade',
      testid: 'next-action-limit-reached',
    };
    if (topStalled) {
      const p = topStalled.property;
      const wc = p.wakacode || '';
      return {
        tone: 'warn',
        icon: TrendingUp,
        title: tt('Stalled listing', 'Listado estancado'),
        message: tt(
          `"${p.title}" has ${topStalled.reveals.count} reveals but 0 messages. Refresh photos or price.`,
          `"${p.title}" tiene ${topStalled.reveals.count} vistas pero 0 mensajes. Mejora fotos o precio.`
        ),
        cta: wc ? `${tt('Refresh', 'Mejorar')} ${wc} →` : tt('Refresh listing →', 'Mejorar listado →'),
        to: `/edit-property/${p.id}`,
        testid: 'next-action-stalled',
      };
    }
    if (!vstatus || vstatus === 'not_started') return {
      tone: 'opportunity',
      icon: ShieldCheck,
      title: tt('Get verified', 'Verifícate'),
      message: tt(
        'Verified agents earn the gold badge and rank higher in search results.',
        'Los agentes verificados ganan la insignia dorada y mejor posición en búsquedas.'
      ),
      cta: tt('Start verification →', 'Iniciar verificación →'),
      to: '/verification',
      testid: 'next-action-start-verification',
    };
    if (profilePct < 70) return {
      tone: 'opportunity',
      icon: Sparkles,
      title: tt('Boost your trust score', 'Aumenta tu puntaje de confianza'),
      message: tt(
        `Your profile is ${profilePct}% complete. Finish to attract more leads.`,
        `Tu perfil está ${profilePct}% completo. Termínalo para atraer más leads.`
      ),
      cta: tt('Complete profile →', 'Completar perfil →'),
      to: '/profile',
      testid: 'next-action-profile-incomplete',
    };
    if (vstatus === 'pending' || vstatus === 'documents_uploaded' || vstatus === 'under_review' || vstatus === 'ai_reviewed') {
      // While waiting for review, suggest enriching the best-performing listing.
      if (topActive) {
        const p = topActive.property;
        return {
          tone: 'info',
          icon: Camera,
          title: tt('Verification in review', 'Verificación en revisión'),
          message: tt(
            `Meanwhile, add more photos to "${p.title}" to capture more leads.`,
            `Mientras tanto, añade más fotos a "${p.title}" para captar más leads.`
          ),
          cta: tt('Edit listing →', 'Editar listado →'),
          to: `/edit-property/${p.id}`,
          testid: 'next-action-pending-enrich',
        };
      }
      return {
        tone: 'info',
        icon: Camera,
        title: tt('Verification in review', 'Verificación en revisión'),
        message: tt(
          "We'll email you when it's done. Meanwhile, add a new listing.",
          'Te avisaremos cuando termine. Mientras tanto, agrega un nuevo listado.'
        ),
        cta: tt('New listing →', 'Nuevo listado →'),
        to: '/submit',
        testid: 'next-action-pending-new',
      };
    }
    // Healthy / verified
    return {
      tone: 'success',
      icon: CheckCircle2,
      title: tt('You\'re in great shape', '¡Vas excelente!'),
      message: tt(
        leadsSummary?.total_reveals > 0
          ? `${leadsSummary.total_reveals} contact reveals across your listings. Keep momentum — add a new property or share your top listing.`
          : 'Everything looks good. Add a new property to grow your reach.',
        leadsSummary?.total_reveals > 0
          ? `${leadsSummary.total_reveals} vistas de contacto en tus listados. Mantén el ritmo — agrega una propiedad o comparte tu mejor listado.`
          : 'Todo se ve bien. Agrega una propiedad para crecer.'
      ),
      cta: tt('New listing →', 'Nuevo listado →'),
      to: '/submit',
      testid: 'next-action-healthy',
    };
  })();

  // Per-recommendation, per-user dismiss memory (resets when the recommendation
  // changes, so a new "urgent" item always shows even if the previous one was dismissed).
  const dismissKey = user?.id ? `${DISMISS_KEY_PREFIX}${user.id}.${rec.testid}` : null;
  useEffect(() => {
    if (!dismissKey) return;
    setDismissed(localStorage.getItem(dismissKey) === '1');
  }, [dismissKey]);

  if (dismissed) return null;

  // Tone → styling
  const TONE = {
    urgent:      { bar: 'from-red-500 to-red-700',        bg: 'bg-red-50',     border: 'border-red-300',     iconBg: 'bg-red-500',     iconText: 'text-white' },
    warn:        { bar: 'from-amber-500 to-amber-600',    bg: 'bg-amber-50',   border: 'border-amber-300',   iconBg: 'bg-amber-500',   iconText: 'text-white' },
    opportunity: { bar: 'from-[#D4AF37] to-[#C8A028]',    bg: 'bg-amber-50/60', border: 'border-[#D4AF37]/40', iconBg: 'bg-[#D4AF37]', iconText: 'text-[#0B192C]' },
    info:        { bar: 'from-blue-400 to-blue-600',      bg: 'bg-blue-50',    border: 'border-blue-200',    iconBg: 'bg-blue-500',    iconText: 'text-white' },
    success:     { bar: 'from-emerald-400 to-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', iconBg: 'bg-emerald-500', iconText: 'text-white' },
  }[rec.tone];
  const Icon = rec.icon;

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dismissKey) localStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  return (
    <Card
      className={`relative overflow-hidden border-2 ${TONE.border} ${TONE.bg} mb-4 transition-all hover:shadow-md`}
      data-testid="next-action-strip"
    >
      {/* Accent bar */}
      <div className={`absolute top-0 left-0 h-1 w-full bg-gradient-to-r ${TONE.bar}`} />
      <CardContent className="p-4 pl-4 pr-3">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${TONE.iconBg} flex items-center justify-center shrink-0`}>
            <Icon className={`w-5 h-5 ${TONE.iconText}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{tt("What's next?", '¿Qué sigue?')}</span>
              <span className="text-sm font-heading font-semibold text-[#0B192C]" data-testid={`${rec.testid}-title`}>
                {rec.title}
              </span>
            </div>
            <p className="text-sm text-slate-700 line-clamp-2" data-testid={`${rec.testid}-message`}>{rec.message}</p>
            <Link
              to={rec.to}
              className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-[#0B192C] hover:text-[#D4AF37] transition"
              data-testid={`${rec.testid}-cta`}
            >
              {rec.cta}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            className="text-slate-400 hover:text-slate-700 transition p-1 -mt-1 -mr-1 shrink-0"
            aria-label="Dismiss"
            data-testid="next-action-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
